import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@ui/components/ui/checkbox';

/** Marks where the live sprint ends and the finished ones begin, in a list that shows both. */
export const PastSprintsDivider = (): ReactElement => {
  const { t } = useTranslation();

  return (
    <div className="mb-4 mt-8 flex items-center gap-3">
      <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {t('tabs.pastSprints')}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
};

/** `shown` must be the sprints actually drawn: a filter can drop the live one and strand the rule. */
export const startsPastSprints = <T extends { sprint: { id: string } }>(
  shown: T[],
  index: number,
  currentSprintId?: string,
): boolean => index > 0 && !!currentSprintId && shown[index - 1]?.sprint.id === currentSprintId;

/** Ticked shows only the latest sprint; base-ui names it from the `<id>-label` element. */
export const ShowLastSprintCheckbox = ({
  id,
  showAll,
  forced = false,
  onShowAllChange,
}: {
  id: string;
  showAll: boolean;
  /** A filter that lists every sprint whatever the box says, so it shows unticked and locked. */
  forced?: boolean;
  onShowAllChange: (showAll: boolean) => void;
}): ReactElement => {
  const { t } = useTranslation();

  return (
    <div className="flex h-8 shrink-0 items-center gap-2">
      <Checkbox
        id={id}
        checked={!showAll && !forced}
        disabled={forced}
        onCheckedChange={value => onShowAllChange(value !== true)}
      />
      <label
        id={`${id}-label`}
        htmlFor={id}
        className={`text-sm font-medium ${forced ? 'text-muted-foreground' : 'cursor-pointer text-foreground'}`}
      >
        {t('showLastSprint')}
      </label>
    </div>
  );
};
